import type { HostMode } from "./host";

/** OIDC settings of the current host (identity contract §1). Values come from `VITE_*` build variables. */
export interface OidcConfig {
  authority: string;
  clientId: string;
  scope: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  silentRedirectUri: string;
  popupRedirectUri: string;
}

export interface IdentityEnv {
  VITE_OIDC_AUTHORITY?: string;
  VITE_OIDC_CLIENT_ID?: string;
  VITE_OIDC_CONSOLE_CLIENT_ID?: string;
  VITE_TENANT_ROOT_DOMAIN?: string;
  VITE_DEV_TENANT?: string;
}

export const identityDefaults = {
  authority: "http://localhost:8080/realms/positive",
  clientId: "positive-web",
  consoleClientId: "positive-operator-console",
  rootDomain: "localhost",
} as const;

/** Callback paths handled before the router starts (see `main.tsx`). */
export const authPaths = {
  callback: "/auth/callback",
  silentCallback: "/auth/silent-callback",
  popupCallback: "/auth/popup-callback",
} as const;

/**
 * Tenant users: `positive-web` with `organization:{slug}` so the token carries exactly the host tenant. The operator
 * console — and an operator's support session on a tenant host (contract §7) — uses `positive-operator-console`
 * without an organization scope.
 */
export function createOidcConfig(mode: HostMode, origin: string, env: IdentityEnv, operator = false): OidcConfig {
  const consoleClient = mode.kind === "console" || operator;
  return {
    authority: env.VITE_OIDC_AUTHORITY ?? identityDefaults.authority,
    clientId: consoleClient
      ? (env.VITE_OIDC_CONSOLE_CLIENT_ID ?? identityDefaults.consoleClientId)
      : (env.VITE_OIDC_CLIENT_ID ?? identityDefaults.clientId),
    scope:
      mode.kind === "tenant" && !consoleClient
        ? `openid profile email organization:${mode.slug}`
        : "openid profile email",
    redirectUri: `${origin}${authPaths.callback}`,
    postLogoutRedirectUri: `${origin}/`,
    silentRedirectUri: `${origin}${authPaths.silentCallback}`,
    popupRedirectUri: `${origin}${authPaths.popupCallback}`,
  };
}
